import React from 'react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Container } from '../../components/ui/Container';
import { Section } from '../../components/ui/Section';
import { GitBranch, Calendar, CheckCircle, Clock, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Changelog() {
  return <Layout data-id="element-702">
      <Section spacing="lg" data-id="element-703">
        <Container size="lg" data-id="element-704">
          {/* Hero */}
          <div className="mb-12 text-center animate-fade-in-up" data-id="element-705">
            <Badge variant="primary" size="lg" className="mb-6" data-id="element-706">
              Version History
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-6" data-id="element-707">
              Changelog
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-708">
              Track updates, new features, and improvements to the
              CyberSecurityProtocol platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8" data-id="element-709">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8" data-id="element-710">
              {/* Current Version */}
              <section className="animate-fade-in-up animation-delay-100" data-id="element-711">
                <VersionCard version="1.0.0" status="current" date="January 2026" title="Public Baseline Edition" description="Initial public release of CyberSecurity Protocol with comprehensive defensive cybersecurity guidance for individuals and organizations." features={['Personal Security Protocol with device-specific guides (Windows, macOS, Linux, iOS, Android)', 'Organization Protocol aligned with NIST CSF 2.0 (Govern, Identify, Protect, Detect, Respond, Recover)', 'Risk Assessment tools for Personal and Organization levels', 'Compliance guides for Global Standards and Indonesia (UU PDP)', 'Tools Library with password managers, MFA, backup, breach check, and privacy tools', 'Incident Response runbooks for account takeover and lost device scenarios', 'Standards alignment with NIST CSF 2.0, ISO/IEC 27001, and CIS Controls v8', 'GDPR-compliant cookie consent and privacy controls', 'WCAG 2.1 AA accessibility compliance', 'Mobile-first responsive design', 'Modern cyber-themed UI with glassmorphism and animations']} data-id="element-712" />
              </section>

              {/* Planned Updates */}
              <section className="animate-fade-in-up animation-delay-200" data-id="element-713">
                <h2 className="text-2xl font-bold text-slate-100 mb-6 font-mono flex items-center" data-id="element-714">
                  <Clock className="h-6 w-6 mr-3 text-amber-400" data-id="element-715" />
                  Planned Updates
                </h2>

                <div className="space-y-6" data-id="element-716">
                  <VersionCard version="1.1.0" status="planned" date="Q2 2026" title="Executive Deck & PDF Playbooks" description="Enhanced downloadable resources for executives and decision-makers." features={['Executive summary dashboards for C-level stakeholders', 'PDF playbooks for offline use and distribution', 'One-page security briefs for board presentations', 'Printable checklists and quick reference guides', 'Enhanced visualization of risk scores and maturity levels']} data-id="element-717" />

                  <VersionCard version="1.2.0" status="planned" date="Q3 2026" title="Regional Expansion" description="Extended coverage for additional regions and languages." features={['Compliance guides for additional countries (India, Philippines, Thailand, Vietnam)', 'Regional threat landscape analysis for Southeast Asia', 'Localization support for Bahasa Indonesia and other languages', 'Country-specific regulatory frameworks and data sovereignty guides', 'Regional cybersecurity cooperation initiatives']} data-id="element-718" />

                  <VersionCard version="2.0.0" status="planned" date="Q4 2026" title="Institutional Adoption Packs" description="Enterprise-grade resources for large-scale organizational deployment." features={['Multi-tenant organization management', 'Custom branding and white-labeling options', 'Advanced reporting and analytics', 'Integration with SIEM and GRC platforms', 'API access for programmatic use', 'Training certification programs', 'Dedicated support channels for institutional partners']} data-id="element-719" />
                </div>
              </section>

              {/* Feedback */}
              <section className="animate-fade-in-up animation-delay-300" data-id="element-720">
                <Card glass padding="lg" className="border-cyan-500/20" data-id="element-721">
                  <div className="flex items-start space-x-4" data-id="element-722">
                    <div className="h-12 w-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-723">
                      <Rocket className="h-6 w-6" data-id="element-724" />
                    </div>
                    <div data-id="element-725">
                      <h3 className="text-lg font-bold text-slate-100 mb-2" data-id="element-726">
                        Help Shape the Future
                      </h3>
                      <p className="text-sm text-slate-400 mb-4" data-id="element-727">
                        Your feedback drives our development roadmap. Share your
                        suggestions, feature requests, and improvement ideas.
                      </p>
                      <Link to="/about/contact" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-semibold transition-colors text-sm" data-id="element-728">
                        Contact Us →
                      </Link>
                    </div>
                  </div>
                </Card>
              </section>
            </div>

            {/* Sidebar */}
            <div className="space-y-6" data-id="element-729">
              <Card glass padding="lg" className="sticky top-24 animate-fade-in-up animation-delay-400" data-id="element-730">
                <h3 className="text-lg font-bold text-slate-100 mb-4 font-mono" data-id="element-731">
                  Quick Links
                </h3>
                <div className="space-y-2" data-id="element-732">
                  <Link to="/about/mission" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-733">
                    Mission & Vision
                  </Link>
                  <Link to="/about/scope" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-734">
                    Scope & Non-Guarantee
                  </Link>
                  <Link to="/about/standards" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-735">
                    Standards Mapping
                  </Link>
                  <Link to="/about/changelog" className="block p-2 rounded bg-cyan-500/10 text-cyan-400 font-medium border border-cyan-500/20" data-id="element-736">
                    Changelog
                  </Link>
                  <Link to="/about/governance" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-737">
                    Governance
                  </Link>
                  <Link to="/about/disclaimer" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-738">
                    Legal Disclaimer
                  </Link>
                  <Link to="/about/contact" className="block p-2 rounded hover:bg-slate-800/50 text-slate-400 hover:text-cyan-400 transition-colors" data-id="element-739">
                    Contact Us
                  </Link>
                </div>
              </Card>

              {/* Current Version Badge */}
              <Card glass padding="md" className="bg-emerald-500/5 border-emerald-500/20" data-id="element-740">
                <div className="flex items-center space-x-3" data-id="element-741">
                  <CheckCircle className="h-5 w-5 text-emerald-400 flex-shrink-0" data-id="element-742" />
                  <div data-id="element-743">
                    <p className="text-xs font-semibold text-emerald-400 uppercase" data-id="element-744">
                      Current Version
                    </p>
                    <p className="text-lg font-bold text-slate-100 font-mono" data-id="element-745">
                      v1.0.0
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
    </Layout>;
}
interface VersionCardProps {
  version: string;
  status: 'current' | 'planned';
  date: string;
  title: string;
  description: string;
  features: string[];
}
function VersionCard({
  version,
  status,
  date,
  title,
  description,
  features
}: VersionCardProps) {
  const statusConfig = {
    current: {
      badge: 'success',
      badgeText: 'Current',
      icon: CheckCircle,
      iconColor: 'text-emerald-400',
      iconBg: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/20'
    },
    planned: {
      badge: 'warning',
      badgeText: 'Planned',
      icon: Clock,
      iconColor: 'text-amber-400',
      iconBg: 'bg-amber-500/10',
      borderColor: 'border-amber-500/20'
    }
  };
  const config = statusConfig[status];
  const Icon = config.icon;
  return <Card glass padding="lg" className={config.borderColor} data-id="element-746">
      <div className="flex items-start justify-between mb-4" data-id="element-747">
        <div className="flex items-center space-x-3" data-id="element-748">
          <div className={`h-12 w-12 rounded-xl ${config.iconBg} flex items-center justify-center ${config.iconColor}`} data-id="element-749">
            <Icon className="h-6 w-6" data-id="element-750" />
          </div>
          <div data-id="element-751">
            <div className="flex items-center space-x-2 mb-1" data-id="element-752">
              <h3 className="text-2xl font-bold text-slate-100 font-mono" data-id="element-753">
                v{version}
              </h3>
              <Badge variant={config.badge as any} size="sm" data-id="element-754">
                {config.badgeText}
              </Badge>
            </div>
            <div className="flex items-center text-xs text-slate-500" data-id="element-755">
              <Calendar className="h-3 w-3 mr-1" data-id="element-756" />
              {date}
            </div>
          </div>
        </div>
      </div>

      <h4 className="text-lg font-bold text-slate-100 mb-2" data-id="element-757">{title}</h4>
      <p className="text-sm text-slate-400 mb-4" data-id="element-758">{description}</p>

      <div data-id="element-759">
        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3" data-id="element-760">
          {status === 'current' ? 'Features' : 'Planned Features'}
        </h5>
        <ul className="space-y-2" data-id="element-761">
          {features.map((feature, index) => <li key={index} className="flex items-start text-sm text-slate-300" data-id="element-762">
              <span className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-763">▸</span>
              <span data-id="element-764">{feature}</span>
            </li>)}
        </ul>
      </div>
    </Card>;
}